import React, { useState, useEffect, useRef } from 'react';
import { UserSession } from '../types';
import {
  Sparkles,
  X,
  Send,
  Bot,
  User,
  ShieldCheck,
  CheckCircle2,
  Database,
  ExternalLink,
  ChevronRight,
  FileText,
  AlertTriangle,
  RotateCcw,
  Activity
} from 'lucide-react';
import { PulseClinicalAiMark } from './PulseClinicalAiMark';

interface GeminiDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserSession;
  contextScreen: string;
  selectedClientId: string | null;
  initialPrompt?: string;
  onSelectClient?: (clientId: string, clientTab?: 'Overview' | 'Auths' | 'Goals' | 'Visits' | 'Clinical Docs' | 'Billing' | 'Notes') => void;
  onNavigateTab?: (tab: string, subTab?: string) => void;
  onOpenEmailDraft?: (clientId?: string) => void;
}

interface PulseAction {
  label: string;
  type: 'client' | 'tab' | 'email_draft';
  clientId?: string;
  clientTab?: 'Overview' | 'Auths' | 'Goals' | 'Visits' | 'Clinical Docs' | 'Billing' | 'Notes';
  tab?: string;
  subTab?: string;
}

interface WorkflowStep {
  step: number;
  title: string;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'BLOCKED' | 'PENDING';
  detail: string;
}

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: string;
  sources?: string[];
  actions?: PulseAction[];
  freshness?: string;
  workflowSteps?: WorkflowStep[];
  emptyCategory?: string;
}

const WorkflowTraceViewer: React.FC<{ steps: WorkflowStep[] }> = ({ steps }) => {
  if (!steps || steps.length === 0) return null;

  const getStatusBadge = (status: WorkflowStep['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold">DONE</span>;
      case 'BLOCKED':
        return <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[9px] font-bold animate-pulse">BLOCKED</span>;
      case 'IN_PROGRESS':
        return <span className="px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30 text-[9px] font-bold">IN PROGRESS</span>;
      case 'PENDING':
      default:
        return <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[9px] font-bold">PENDING</span>;
    }
  };

  return (
    <div className="mt-3 p-2.5 rounded-lg bg-slate-950/70 border border-slate-800/80 space-y-2">
      <div className="text-[11px] font-semibold text-sky-400 flex items-center space-x-1.5">
        <Activity className="w-3.5 h-3.5 text-sky-400" />
        <span>Encounter Lifecycle Workflow Trace</span>
      </div>
      <div className="space-y-1.5">
        {steps.map((s) => (
          <div key={s.step} className="flex items-start justify-between p-1.5 rounded bg-slate-900/60 border border-slate-800/50 text-[11px]">
            <div className="flex items-start space-x-2">
              <span className="w-4 h-4 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center text-[10px] font-mono shrink-0 mt-0.5">
                {s.step}
              </span>
              <div>
                <span className="font-medium text-slate-200">{s.title}</span>
                <p className="text-[10px] text-slate-400 mt-0.5">{s.detail}</p>
              </div>
            </div>
            <div className="shrink-0 ml-2">
              {getStatusBadge(s.status)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SourcesAccordion: React.FC<{ sources: string[] }> = ({ sources }) => {
  const [open, setOpen] = useState(false);
  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-2.5 pt-2 border-t border-slate-800/60">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center space-x-1 text-[11px] text-slate-400 hover:text-slate-200 transition-colors py-0.5 focus:outline-none select-none"
      >
        <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform duration-150 ${open ? 'rotate-90 text-sky-400' : ''}`} />
        <span className="font-medium">Sources ({sources.length})</span>
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5 pl-2 border-l border-slate-700/50">
          {sources.map((s, idx) => (
            <span
              key={idx}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-950/80 text-slate-300 border border-slate-800 flex items-center space-x-1"
            >
              <Database className="w-2.5 h-2.5 text-sky-400 shrink-0" />
              <span>{s}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const GeminiDrawer: React.FC<GeminiDrawerProps> = ({
  isOpen,
  onClose,
  user,
  contextScreen,
  selectedClientId,
  initialPrompt = '',
  onSelectClient,
  onNavigateTab,
  onOpenEmailDraft
}) => {
  const [inputPrompt, setInputPrompt] = useState(initialPrompt);
  const [sessionContext, setSessionContext] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      sender: 'ai',
      text: `Hello ${user.name}. I am Pulse Intelligence (Allegiant AI), connected to your authoritative EMR database. I can answer operational questions regarding authorizations, plans of care, pending signatures, billing holds, and client charts.`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sources: ['Cloud SQL Canonical Schema', 'RBAC Security Boundary']
    }
  ]);
  const [loading, setLoading] = useState(false);
  const lastAutoPromptRef = React.useRef<string | null>(null);

  const handleSendMessage = async (promptToSend?: string) => {
    const query = promptToSend || inputPrompt;
    if (!query.trim() || loading) return;

    const userMsg: Message = {
      id: 'usr-' + Date.now(),
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputPrompt('');
    setLoading(true);

    try {
      const token = typeof window !== 'undefined'
        ? (localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token'))
        : null;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }

      const res = await fetch('/api/gemini/chat', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          prompt: query,
          contextScreen,
          selectedClientId,
          conversationContext: sessionContext
        })
      });

      if (res.ok) {
        let data: any;
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          data = await res.json();
        } else {
          const rawText = await res.text();
          try {
            data = JSON.parse(rawText);
          } catch {
            data = { response: rawText };
          }
        }

        if (data.sessionContext) {
          setSessionContext(data.sessionContext);
        }
        const aiMsg: Message = {
          id: 'ai-' + Date.now(),
          sender: 'ai',
          text: data.response || data.text || 'No response generated.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sources: data.sources || ['Canonical Cloud SQL Database'],
          actions: data.actions || [],
          freshness: data.freshness,
          workflowSteps: data.workflowSteps,
          emptyCategory: data.emptyCategory
        };
        setMessages((prev) => [...prev, aiMsg]);
      } else {
        let errorMsg = 'Failed to retrieve AI analysis.';
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          try {
            const err = await res.json();
            errorMsg = err.error || errorMsg;
          } catch {
            errorMsg = `Server error (HTTP ${res.status})`;
          }
        } else {
          errorMsg = `Server error (HTTP ${res.status})`;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: 'err-' + Date.now(),
            sender: 'ai',
            text: `Query error: ${errorMsg}`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: 'err-' + Date.now(),
          sender: 'ai',
          text: `Network error connecting to Allegiant AI: ${err.message}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const drawerRef = useRef<HTMLDivElement>(null);

  // Desktop click-outside handling for Owner and Referral Coordinator roles
  useEffect(() => {
    if (!isOpen) return;

    const isDesktopEligibleRole = user.role === 'OWNER' || user.role === 'REFERRAL_COORDINATOR';
    if (!isDesktopEligibleRole) return;

    // Disarm on the first frame to prevent the opening click from immediately closing the panel
    let isArmed = false;
    const armTimer = setTimeout(() => {
      isArmed = true;
    }, 60);

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (!isArmed) return;

      // Hard freeze mobile behavior — apply strictly at desktop breakpoint (>= 768px)
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        return;
      }

      const target = e.target as Node | null;
      if (!target) return;

      // Clicks INSIDE the Pulse Intelligence panel must NOT close it
      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }

      // If clicking the sidebar AI Assist button, allow its toggle handler to run
      if (target instanceof Element && target.closest('#sidebar-ai-assist-cta, [data-ai-assist-trigger]')) {
        return;
      }

      // Outside click: close Pulse Intelligence side panel
      onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen, user.role, onClose]);

  // Auto-send initial prompt if triggered from search bar
  React.useEffect(() => {
    if (isOpen && initialPrompt && initialPrompt.trim() && lastAutoPromptRef.current !== initialPrompt) {
      lastAutoPromptRef.current = initialPrompt;
      setInputPrompt(initialPrompt);
      handleSendMessage(initialPrompt);
    }
  }, [isOpen, initialPrompt]);

  if (!isOpen) return null;

  // Role-appropriate quick prompt chips
  const getQuickChips = () => {
    switch (user.role) {
      case 'OWNER':
      case 'ADMIN':
        return [
          'What needs attention today?',
          'Expiring auths (< 4 units)',
          'Why is Mickey on billing hold?',
          'What is holding this note up?',
          'Which POCs expire in the next 30 days?',
          'Which note mentions AAC?',
          'Compare last two POCs for Mickey',
          'Overdue guardian signatures (>24h)'
        ];
      case 'REFERRAL_COORDINATOR':
      case 'OFFICE':
        return [
          'Expiring auths (< 4 units)',
          'Which POCs expire in the next 30 days?',
          'Who has no active authorization?',
          'Which clients are missing a POC?',
          'Why does Levi have no active auth?',
          'Which guardian signatures are overdue?'
        ];
      case 'SUPERVISOR':
        return [
          'Why does Marg need supervisor signature?',
          'What is holding this note up?',
          'Which therapists have pending supervision?',
          'Which guardian signatures are overdue?',
          'What needs attention today?'
        ];
      case 'ASSISTANT':
        return [
          'My pending clinical notes',
          'Summarize active client goals',
          'Show my assigned clients'
        ];
      default:
        return [
          'What needs attention today?',
          'Expiring auths (< 4 units)',
          'Which POCs expire in the next 30 days?'
        ];
    }
  };

  const quickChips = getQuickChips();

  const handleActionClick = (action: PulseAction) => {
    if (action.type === 'client' && action.clientId) {
      onSelectClient?.(action.clientId, action.clientTab);
      onNavigateTab?.('clients', action.clientTab);
    } else if (action.type === 'tab' && action.tab) {
      onNavigateTab?.(action.tab, action.subTab);
    } else if (action.type === 'email_draft') {
      onOpenEmailDraft?.(action.clientId);
    }
  };

  const isOwnerOrRC = user.role === 'OWNER' || user.role === 'REFERRAL_COORDINATOR';

  return (
    <div
      ref={drawerRef}
      id="pulse-intelligence-drawer"
      className="fixed inset-y-0 right-0 w-full sm:w-[500px] z-50 glass-panel bg-[#0c0e12]/95 border-l border-sky-500/30 flex flex-col shadow-2xl backdrop-blur-xl animate-in slide-in-from-right duration-200"
    >
      {/* Drawer Header */}
      <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-sky-950/30">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-lg bg-sky-500/20 border border-sky-500/40 flex items-center justify-center text-sky-400 overflow-hidden shrink-0">
            <PulseClinicalAiMark size={22} />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-xs font-bold uppercase tracking-wider text-white">
                Pulse Intelligence
              </span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20">
                Live EMR Grounded
              </span>
              {sessionContext?.lastClientName && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center space-x-1">
                  <span>Chart: {sessionContext.lastClientName}</span>
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-400 flex items-center space-x-1.5 mt-0.5">
              <span>RBAC:</span>
              <span className="text-sky-300 font-semibold">{user.role}</span>
              <span>• Scope: {contextScreen || 'Dashboard'}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          {sessionContext && (
            <button
              onClick={() => {
                setSessionContext(null);
                setMessages((prev) => [
                  ...prev,
                  {
                    id: 'ctx-reset-' + Date.now(),
                    sender: 'ai',
                    text: 'Session context reset. You can now query any client or operational domain.',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    sources: ['Context Reset']
                  }
                ]);
              }}
              title="Reset conversation context"
              className="p-1.5 rounded-lg text-slate-400 hover:text-sky-300 hover:bg-white/5 transition-colors text-[11px] flex items-center space-x-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            title="Close Pulse Assistant"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick Prompts Bar */}
      <div className="p-3 bg-slate-900/50 border-b border-slate-800 flex gap-2 overflow-x-auto select-none no-scrollbar">
        {quickChips.map((chip, i) => (
          <button
            key={i}
            onClick={() => handleSendMessage(chip)}
            className="whitespace-nowrap px-2.5 py-1 rounded-full bg-sky-500/10 hover:bg-sky-500/25 border border-sky-500/30 text-sky-300 text-[11px] transition-all font-medium flex items-center space-x-1 shrink-0"
          >
            <span>{chip}</span>
          </button>
        ))}
      </div>

      {/* Messages Scroll Area */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[92%] rounded-xl p-3 text-xs leading-relaxed ${
                m.sender === 'user'
                  ? 'bg-sky-600 text-white shadow-md'
                  : 'bg-slate-900/90 text-slate-200 border border-slate-800 shadow-sm'
              }`}
            >
              {m.sender === 'ai' && (
                <div className="flex items-center justify-between text-[10px] text-sky-400 font-bold uppercase mb-1.5 pb-1 border-b border-slate-800/60">
                  <div className="flex items-center space-x-1.5">
                    <Bot className="w-3.5 h-3.5 text-sky-400" />
                    <span>Allegiant Intelligence · Canonical EMR</span>
                  </div>
                  {m.freshness && (
                    <span className="text-[9px] font-normal text-slate-400 lowercase font-mono">
                      {m.freshness}
                    </span>
                  )}
                </div>
              )}
              <div className="whitespace-pre-wrap font-sans">{m.text}</div>

              {/* Workflow Step Trace */}
              {m.workflowSteps && m.workflowSteps.length > 0 && (
                <WorkflowTraceViewer steps={m.workflowSteps} />
              )}

              {/* Action Buttons */}
              {m.actions && m.actions.length > 0 && (
                <div className="mt-3 pt-2.5 border-t border-slate-800/80 flex flex-wrap gap-1.5">
                  {m.actions.map((act, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleActionClick(act)}
                      className="px-2 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/30 border border-sky-500/30 text-sky-200 text-[11px] font-medium flex items-center space-x-1 transition-all group"
                    >
                      <ChevronRight className="w-3 h-3 text-sky-400 group-hover:translate-x-0.5 transition-transform" />
                      <span>{act.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Sources Citation */}
              {m.sources && m.sources.length > 0 && (
                <SourcesAccordion sources={m.sources} />
              )}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 px-1">{m.timestamp}</span>
          </div>
        ))}

        {loading && (
          <div className="flex items-center space-x-2 text-xs text-sky-400 bg-slate-900/80 p-3 rounded-xl border border-slate-800 animate-pulse w-fit">
            <Sparkles className="w-4 h-4 animate-spin text-sky-400" />
            <span>Consulting the chart database...</span>
          </div>
        )}
      </div>

      {/* Input Box */}
      <div className="p-4 border-t border-slate-800 bg-[#0c0e12]/95">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="relative"
        >
          <input
            type="text"
            value={inputPrompt}
            onChange={(e) => setInputPrompt(e.target.value)}
            placeholder="Ask about authorizations, POCs, signatures, billing holds..."
            className="w-full glass-input rounded-xl pl-4 pr-11 py-2.5 text-xs text-slate-100 placeholder-slate-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 bg-slate-900/90 border-slate-700"
          />
          <button
            type="submit"
            disabled={!inputPrompt.trim() || loading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white transition-all shadow"
            title="Send query"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
        <div className="text-[10px] text-slate-400 mt-2 flex justify-between">
          <span>Read-only • Grounded in live EMR state</span>
          <span>Zero Client API Keys</span>
        </div>
      </div>
    </div>
  );
};

